import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

function ContactPage() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Opens the user's mail client with pre-filled subject + body
    const subject = encodeURIComponent(`Website inquiry from ${formData.name || 'Visitor'}`);
    const body = encodeURIComponent(
      `${formData.message}\n\n— ${formData.name}\n${formData.email}`
    );
    window.location.href = `mailto:info@soccersphere.net?subject=${subject}&body=${body}`;
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1 pt-24 pb-16">
        {/* Hero */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-12">
          <div className="inline-block px-4 py-2 bg-gradient-to-r from-green-700 to-green-600 text-white rounded-full text-sm font-semibold mb-4">
            Contact Us
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Let's Talk Soccer
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Questions about a program, tryouts, or partnering with your school? Reach out — we usually respond within one business day.
          </p>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Phone - the most prominent block */}
              <a
                href="tel:+16692045870"
                className="block group bg-gradient-to-br from-green-700 to-green-600 rounded-2xl p-8 text-white shadow-xl hover:shadow-2xl transition"
                data-testid="contact-phone-card"
              >
                <div className="flex items-center space-x-3 mb-3 opacity-90">
                  <Phone className="w-5 h-5" />
                  <span className="text-sm font-semibold uppercase tracking-wider">Call us</span>
                </div>
                <div className="text-3xl md:text-4xl font-bold group-hover:scale-[1.02] transition-transform origin-left">
                  +1 (669) 204-5870
                </div>
                <p className="mt-3 text-green-50/90 text-sm">
                  Fastest way to reach a coach. Tap to call.
                </p>
              </a>

              {/* Email */}
              <a
                href="mailto:info@soccersphere.net"
                className="block bg-white border border-gray-200 rounded-2xl p-6 hover:border-green-600 transition"
                data-testid="contact-email-card"
              >
                <div className="flex items-center space-x-3 mb-2 text-slate-500">
                  <Mail className="w-5 h-5" />
                  <span className="text-sm font-semibold uppercase tracking-wider">Email</span>
                </div>
                <div className="text-lg font-semibold text-slate-900">info@soccersphere.net</div>
              </a>

              {/* Address */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <div className="flex items-center space-x-3 mb-2 text-slate-500">
                  <MapPin className="w-5 h-5" />
                  <span className="text-sm font-semibold uppercase tracking-wider">Visit</span>
                </div>
                <div className="text-slate-900 leading-relaxed">
                  2010 El Camino Real #1418<br />
                  Santa Clara, CA 95050
                </div>
              </div>
            </div>

            {/* Message Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm" id="contact-form">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Send Us a Message</h2>
                <p className="text-slate-600 mb-6">
                  Prefer writing it out? Drop us a note and we'll get back to you.
                </p>

                {submitted ? (
                  <div className="flex items-center space-x-3 bg-green-50 border border-green-200 text-green-800 rounded-xl p-4" data-testid="contact-form-success">
                    <CheckCircle className="w-6 h-6 flex-shrink-0" />
                    <span>Thanks — your email client should be opening now.</span>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Name</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none transition"
                        placeholder="Your full name"
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none transition"
                        placeholder="you@example.com"
                        data-testid="contact-email-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Message</label>
                      <textarea
                        required
                        rows={6}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none transition resize-none"
                        placeholder="Tell us about your player, program interest, or question..."
                        data-testid="contact-message-input"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full inline-flex items-center justify-center bg-gradient-to-r from-green-700 to-green-600 text-white px-6 py-4 rounded-lg text-lg font-semibold hover:opacity-90 transition"
                      data-testid="contact-submit-btn"
                    >
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default ContactPage;
